import { Component, Injector, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

import { BehaviorSubject, Subscription, combineLatest, filter, take, takeWhile, tap } from 'rxjs';

import { PartialMentionApiModel } from '../../../../api/mentions/model/mention.model';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';

import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { Mention } from '../../models/mention';
import { MentionInsightsExplorerService } from '../../services/mention-insights-explorer.service';
import { MentionInsightModel } from '../../models/mention-insight.model';
import { MentionWorkbenchDialogComponent, MentionWorkbenchTabIds } from '../MentionWorkbenchDialog/mention-workbench-dialog.component';
import { messageSeverityEnum } from '../../../Misc/enums/message-severity.enum';
import { MonitorConfigService } from '@Common/MonitorShared/services/monitor-config.service';
import { promptLanguageOptions } from '../../../Forms/data/prompt-language-options';
import { PromptModel, PromptResponseTypesEnum } from '../../models/prompt.model';
import { ResourceActionTypes } from '../../../Basic/enums/resource-actions.enum';
import { TabComponent } from '../../../Layout/components/Tab/tab.component';

import { MentionsService } from '../../../../Monitor/Core/services/mentions.service';
import { InsightEntryModel } from '../../models/insight.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MentionInsightTypes } from '@Common/Mentions/enums/mention-insight-types.enum';


enum MentionInsightsExplorerTabInisightTabTypesEnum {
    QUICK_INSIGHTS,
    CUSTOM,
    SAVED_INSIGHTS
}

@Component({
    selector: 'agpr-mention-insights-explorer-tab',
    templateUrl: './mention-insights-explorer-tab.component.html',
    styleUrls: ['./mention-insights-explorer-tab.component.scss'],
    providers: [
        {
            provide: TabComponent,
            useExisting: MentionInsightsExplorerTabComponent
        }
    ],
    standalone: false
})
export class MentionInsightsExplorerTabComponent extends TabComponent implements OnInit, OnDestroy {
    readonly insightTabTypes = MentionInsightsExplorerTabInisightTabTypesEnum;
    readonly messageSeverities = messageSeverityEnum;
    readonly buttonTypes = ButtonTypesEnum;
    readonly promptLanguageOptions = promptLanguageOptions;
    readonly promptResponseTypes = PromptResponseTypesEnum;
    readonly promptNoResetFields = ['mentionId', 'type', 'responseType', 'keywords', 'oldKeywords', 'formGroup', 'keywordValueChangeSubscription'];

    @ViewChild('tabFooter') declare footer: TemplateRef<any>;

    mention: Mention;
    selectedTabIndex: number;
    language: string;
    prompts: PromptModel[];
    busy: boolean = false;
    hasMentionInsightsEnabled: boolean = false;
    subscriptions: Subscription[] = [];

    languageFormControl: FormControl<DropdownOptionModel<any>> = new FormControl<DropdownOptionModel<any>>(null);
    customPrompts: PromptModel[] = [];

    constructor(
        private mentionsService: MentionsService,
        private coreDialogService: CoreDialogService,
        private mentionInsightsExplorerService: MentionInsightsExplorerService,
        private localizationService: LocalizationService,
        private monitorConfigService: MonitorConfigService,
        public injector: Injector,
        public currentUserStateService: CurrentUserStateService
    ) {
        super();
        this.subscriptions.push(
            this.mentionInsightsExplorerService.getLanguage().pipe(filter(x => !!x)).subscribe(language => {
                this.language = language;
                this.languageFormControl.setValue(this.promptLanguageOptions.find(x => x.value === language));
            }),
            this.languageFormControl.valueChanges.pipe(filter(x => !!x))
                .subscribe(option => this.mentionInsightsExplorerService.setLanguage(option.value, false)),
            combineLatest([
                this.mentionInsightsExplorerService.getSelectedMention().pipe(
                    filter(x => !!x),
                    tap(mention => {
                        this.mention = mention;
                        if (this.mention?.canGenerateInsight) {
                            this.languageFormControl.enable();
                        } else {
                            this.languageFormControl.disable();
                        }
                    })
                ),
                this.mentionInsightsExplorerService.getSelectedPrompts().pipe(
                    filter(x => !!x),
                    tap(prompts => {
                        this.prompts?.forEach(x =>x.keywordValueChangeSubscription?.unsubscribe());
                        this.prompts = prompts;
                        this.prompts.filter(x => !!x.formGroup).forEach(x => {
                            x.keywordValueChangeSubscription = x.formGroup.get('keywords').valueChanges.subscribe(keywords => x.keywords = keywords);
                        });
                    })
                )
            ]).subscribe(([mention, prompts]) => {
                prompts.filter(x => !!x.formGroup)?.forEach(prompt => {
                    let keywords = prompt.keywords;
                    if (!prompt.oldKeywords && (prompt.mentionId === mention.id || prompt.mentionId === mention.mentionId)) {
                        keywords = mention.contextualKeywords;
                        prompt.keywords = keywords.slice();
                        prompt.oldKeywords = keywords.slice();
                    }
                    prompt.formGroup.get('keywords').setValue(keywords.slice());
                });
                prompts.forEach(prompt => {
                    prompt.insightSaved = false;
                    mention.insights?.forEach(insight => {
                        if (this.localizationService.transform('Mention.workbench.insights.system.' + prompt.type) === insight.oldName
                            && (prompt.insightText === insight.oldContent ||
                            this.mentionInsightsExplorerService.generateInsightContent(prompt) === insight.oldContent)
                        ) {
                            prompt.insightSaved = true;
                        }
                    });
                });
            }),
            this.monitorConfigService.getHasMentionInsightsEnabled().subscribe(x => this.hasMentionInsightsEnabled = x),
            this.currentUserStateService.getUILanguageTag().subscribe(tag => {
                this.promptLanguageOptions.forEach(option => {
                    option.label = tag + '.' + option.value;
                });
            }),
            this.mentionInsightsExplorerService.customPrompts$.subscribe(x => this.customPrompts = x)
        );
    }

    ngOnInit(): void {
        this.selectedTabIndex = MentionInsightsExplorerTabInisightTabTypesEnum.QUICK_INSIGHTS;
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    tabChange(index: number): void {
        this.selectedTabIndex = index;
    }

    generateInsight(prompt: PromptModel, isRegenerate: boolean = false): void {
        if (!this.hasMentionInsightsEnabled ||
            ((prompt.insightText || prompt.insightModel ) && !isRegenerate) ||
            prompt.formGroup?.invalid
        ) {
            return;
        }

        this.busy = true;
        prompt.generating = true;
        if (!prompt.isGenerating$) {
            prompt.isGenerating$ = new BehaviorSubject<boolean>(true);
        } else {
            prompt.isGenerating$.next(true);
        }

        this.mentionInsightsExplorerService.generateInsight(prompt);
        prompt.isGenerating$.pipe(
            takeWhile(isGenerating => {
                if (!isGenerating) {
                    this.busy = false;
                    prompt.generating = false;
                    prompt.isGenerating$ = null;
                    if (isRegenerate) {
                        prompt.insightSaved = false;
                    }
                    if (prompt.keywords) {
                        prompt.oldKeywords = prompt.keywords.slice();
                    }
                    this.updateLiveRegion();
                }

                return isGenerating;
            })
        ).subscribe();
    }

    generateEntryInsight(event: { categoryName: string; entry: InsightEntryModel; prompt: PromptModel }): void {
        const categoryName = event.categoryName;
        const entry = event.entry as InsightEntryModel;
        const prompt = event.prompt as PromptModel;

        this.busy = true;
        entry.generating = true;
        prompt.generating = true;

        if (!entry.insight) {
            entry.expanded = true;
        }

        if (!entry.isGenerating$) {
            entry.isGenerating$ = new BehaviorSubject<boolean>(true);
        } else {
            entry.isGenerating$.next(true);
        }

        this.mentionInsightsExplorerService.generateEntryInsight(categoryName, entry, prompt);

        entry.isGenerating$.pipe(
            takeWhile(isGenerating => {
                if (!isGenerating) {
                    this.busy = false;
                    entry.generating = false;
                    entry.isGenerating$ = null;
                    prompt.generating = false;
                    this.updateLiveRegion();
                }

                return isGenerating;
            })
        ).subscribe();
    }

    cancelGeneration(prompt: PromptModel) {
        this.mentionInsightsExplorerService.abortFetching();
        if (prompt.type === MentionInsightTypes.CUSTOM) {
            prompt.insightText = '';
            prompt.insightModel = null;
            prompt.generating = false;
        } else {
            this.resetPrompt(prompt);
        }
        this.busy = false;
    }

    resetPrompt(prompt: any) {
        Object.keys(prompt).forEach(key => {
            if (!this.promptNoResetFields.includes(key)) {
                delete prompt[key];
            }
        });
    }

    saveNewInsight(prompt: PromptModel): void {
        const newInsight = new MentionInsightModel();
        newInsight.name = this.localizationService.transform('Mention.workbench.insights.system.' + prompt.type);
        newInsight.content = this.mentionInsightsExplorerService.generateInsightContent(prompt);
        if (!this.mention.insights?.length) {
            this.mention.insights = [];
            newInsight.isPreferred = true;
        }

        this.mention.insights.push(newInsight);

        this.persistInsights(newInsight, ResourceActionTypes.SAVE, prompt);
    }

    preferInsight(insight: MentionInsightModel): void{
        this.mention.insights.forEach(item => {
            if (item.id === insight.id) {
                item.isPreferred = true;
            } else {
                item.isPreferred = false;
            }
        });

        this.persistInsights(insight, ResourceActionTypes.UPDATE);
    }

    updateInsight(insight: MentionInsightModel): void{
        this.persistInsights(insight, ResourceActionTypes.UPDATE);
    }

    deleteInsight(insight: MentionInsightModel): void {
        this.mention.insights = this.mention.insights.filter(item => item.id !== insight.id);

        if (this.mention.insights.length === 1) {
            this.mention.insights[0].isPreferred = true;
        }

        this.persistInsights(insight, ResourceActionTypes.DELETE);
    }

    persistInsights(insight: MentionInsightModel, actionType: ResourceActionTypes, prompt?: PromptModel): void {
        const patch: PartialMentionApiModel = {
            id: this.mention.mentionId,
            insights: this.mention.insights?.map(x => x.toApiModel())
        };

        this.mentionsService.updateMentionInsights(patch, insight.name, actionType).subscribe(done => {
            if (done) {
                switch (actionType) {
                    case ResourceActionTypes.SAVE:
                        prompt.insightSaved = true;
                        break;
                    case ResourceActionTypes.UPDATE:
                        this.prompts.filter(x => x.insightSaved).forEach(x => {
                            if (this.localizationService.transform('Mention.workbench.insights.system.' + x.type) === insight.oldName
                                && x.insightText === insight.oldContent) {
                                x.insightSaved = false;
                            }
                        });
                        insight.oldName = insight.name;
                        insight.oldContent = insight.content;
                        break;
                    case ResourceActionTypes.DELETE:
                        const promptWithDeletedInsight = this.prompts.find(x => x.insightText === insight.content);
                        if (promptWithDeletedInsight) {
                            promptWithDeletedInsight.insightSaved = false;
                        }
                        break;
                }
            }
        });
    }

    openInsightCreator(isNewCustomInsight?: boolean, prompt?: PromptModel): void {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                mention: this.mention,
                initialTab: MentionWorkbenchTabIds.INSIGHTS,
                isNewCustomInsight: isNewCustomInsight,
                customPrompt: prompt
            }
        };

        this.coreDialogService.open(MentionWorkbenchDialogComponent, ioModel, this.injector);
    }

    updateLiveRegion(): void {
        document.getElementById('mentionInsightsExplorerTab_liveRegion').textContent = '';
        setTimeout(() => {
            document.getElementById('mentionInsightsExplorerTab_liveRegion').textContent =
                this.localizationService.transform('Mention.insightsExplorerTab.liveRegionText');
        }, 300);
    }

    isNewKeywords(prompt: PromptModel): boolean {
        return JSON.stringify(prompt.oldKeywords) !== JSON.stringify(prompt.formGroup?.get('keywords').value);
    }
}
